// src/components/AssessmentIntro.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import translations from './translations';
import './AssessmentIntro.css';

function AssessmentIntro() {
  const API_BASE_URL = process.env.REACT_APP_TPES_API_BASE_URL;
  const accesstoken = process.env.REACT_APP_TPES_API_TOKEN;
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
   // 从 URL 查询参数中获取语言，默认为簡体中文
  const defaultLang = searchParams.get('defaultLang') || 'zh-CN';
  const t = translations[defaultLang] || translations['zh-CN'];
  const [isStarting, setIsStarting] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);

  const handleStart = async () => {
    setIsStarting(true);
    
    try {
      // 从localStorage获取用户信息与当前二维码参数
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const { useruuid, evaluation_uuid } = userInfo;

      const urlParams = new URLSearchParams(window.location.search);
      const companyCode = urlParams.get('companyCode') || '';
      const jobNID = urlParams.get('Job_NID') || '';
      const codeType = urlParams.get('codeType') || '';
      const evaluationType = urlParams.get('evaluation_type') || '';
      const orderKey = `quizOrder_${defaultLang}`;

      let sessions;
      try { sessions = JSON.parse(localStorage.getItem('userEvaluationSessions') || '{}'); } catch { sessions = {}; }
      const prev = useruuid ? sessions[useruuid] : undefined;
      const hasJobParams = !!(companyCode || jobNID || codeType || evaluationType);
      const existingAnswers = (() => { try { return JSON.parse(localStorage.getItem('quizAnswers')) || {}; } catch { return {}; } })();
      const hasExistingAnswers = Object.keys(existingAnswers).length > 0;

      // 1) 先处理存在历史作答的情况：根据二维码是否相同决定清理或接续
      if (hasExistingAnswers) {
        if (useruuid) {
          const isSame =
            prev &&
            prev.companyCode === companyCode &&
            prev.Job_NID === jobNID &&
            prev.codeType === codeType &&
            prev.evaluation_type === evaluationType;

          // 若存在岗位二维码且不同，则清理本地并直接进入新测评
          if (hasJobParams && (!prev || !isSame)) {
            localStorage.removeItem('quizAnswers');
            localStorage.removeItem(orderKey);
            localStorage.removeItem('startTime');
            localStorage.removeItem('timeTaken');
            sessions[useruuid] = { companyCode, Job_NID: jobNID, codeType, evaluation_type: evaluationType };
            localStorage.setItem('userEvaluationSessions', JSON.stringify(sessions));
            navigate(`/quiz?defaultLang=${defaultLang}`);
            return;
          }
          
          // 如果有 evaluation_uuid，检查后端状态是否有效
          if (evaluation_uuid) {
            try {
              const statusResponse = await axios.get(`${API_BASE_URL}/api/assessment/evaluation_status`, {
                params: {
                  useruuid: useruuid,
                  evaluation_uuid: evaluation_uuid
                },
                headers: {
                  Authorization: `Bearer ${accesstoken}`,
                  'Content-Type': 'application/json',
                },
              });
              
              if (statusResponse.status === 200) {
                const status = statusResponse.data.status;
                // 如果测评不存在或已完成，强制重新开始
                if (status === 'no' || status === 'complete') {
                  // 调用 handleRestart 逻辑（但这里是在 handleStart 内部，直接复用逻辑或跳转）
                  // 清理答题数据
                  localStorage.removeItem('quizAnswers');
                  localStorage.removeItem(orderKey);
                  localStorage.removeItem('startTime');
                  localStorage.removeItem('timeTaken');
                  
                  // 构造跳转参数
                  const params = new URLSearchParams();
                  params.append('useruuid', useruuid);
                  params.append('defaultLang', defaultLang);
                  params.append('is_new', 'true');
                  
                  if (companyCode) params.append('companyCode', companyCode);
                  if (jobNID) params.append('Job_NID', jobNID);
                  if (codeType) params.append('codeType', codeType);
                  if (evaluationType) params.append('evaluation_type', evaluationType);
                  
                  // 直接跳转到 UserInfoForm
                  navigate(`/interview?${params.toString()}`);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to check evaluation status, proceeding with local check', err);
              // 网络错误等情况，暂时允许继续或根据需求处理
            }
          }
        }
        // 二维码相同或无岗位参数：弹出接续对话框并结束，不调用后端
        setShowResumeDialog(true);
        return;
      }

      // 2) 无历史答案：更新会话并调用后端 start_evaluation，随后进入测评
      if (useruuid) {
        sessions[useruuid] = { companyCode, Job_NID: jobNID, codeType, evaluation_type: evaluationType };
        localStorage.setItem('userEvaluationSessions', JSON.stringify(sessions));
      }

      if (useruuid && evaluation_uuid) {
        // 调用start_evaluation API更新状态
        // const API_BASE_URL = 'http://localhost:7000';
        const API_BASE_URL = process.env.REACT_APP_TPES_API_BASE_URL;
        const accessToken = process.env.REACT_APP_TPES_API_TOKEN;
        
        await axios.post(`${API_BASE_URL}/api/assessment/start_evaluation`, {
          useruuid: useruuid,
          evaluation_uuid: evaluation_uuid
        }, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        
      }

      navigate(`/quiz?defaultLang=${defaultLang}`);
    } catch (error) {
      const existingAnswers = (() => { try { return JSON.parse(localStorage.getItem('quizAnswers')) || {}; } catch { return {}; } })();
      if (Object.keys(existingAnswers).length > 0) {
        setShowResumeDialog(true);
      } else {
        navigate(`/quiz?defaultLang=${defaultLang}`);
      }
    } finally {
      setIsStarting(false);
    }
  };

  const handleRestart = async () => {
    // 避免重复点击
    if (isStarting) return;
    setIsStarting(true);
    
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const useruuid = userInfo.useruuid;
      
      // 如果没有useruuid，说明数据异常，清除数据并返回首页
      if (!useruuid) {
        localStorage.clear();
        navigate('/');
        return;
      }

      // 获取当前 URL 参数或 Session 中的参数
      const urlParams = new URLSearchParams(window.location.search);
      let companyCode = urlParams.get('companyCode');
      let jobNID = urlParams.get('Job_NID');
      let codeType = urlParams.get('codeType');
      let evaluationType = urlParams.get('evaluation_type');

      // 如果 URL 中没有参数，尝试从 Session 中获取
      if (!companyCode && !jobNID) {
        try {
          const sessions = JSON.parse(localStorage.getItem('userEvaluationSessions') || '{}');
          const session = sessions[useruuid];
          if (session) {
            companyCode = session.companyCode;
            jobNID = session.Job_NID;
            codeType = session.codeType;
            evaluationType = session.evaluation_type;
          }
        } catch (e) {
          console.error('Error reading sessions:', e);
        }
      }

      // 清理答题数据
      const orderKey = `quizOrder_${defaultLang}`;
      localStorage.removeItem('quizAnswers');
      localStorage.removeItem(orderKey);
      localStorage.removeItem('startTime');
      localStorage.removeItem('timeTaken');
      
      // 注意：这里我们不清除 userEvaluationSessions，因为 UserInfoForm 会在提交时更新它
      // 我们也不需要手动创建 session，UserInfoForm 会处理

      // 构造跳转参数
      const params = new URLSearchParams();
      params.append('useruuid', useruuid);
      params.append('defaultLang', defaultLang);
      params.append('is_new', 'true'); // 强制 UserInfoForm 显示表单
      
      if (companyCode) params.append('companyCode', companyCode);
      if (jobNID) params.append('Job_NID', jobNID);
      if (codeType) params.append('codeType', codeType);
      if (evaluationType) params.append('evaluation_type', evaluationType);

      // 跳转回 UserInfoForm
      setShowResumeDialog(false);
      navigate(`/interview?${params.toString()}`);
      
    } catch (error) {
      console.error('Restart error:', error);
      alert('重新开始失败，请刷新页面重试');
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="assessment-intro">
      <h1>{t.assessmentTitle}</h1>
      <ul className="assessment-notes">
        <li>{t.networkNote}</li>
        <li>{t.focusNote}</li>
        <li>{t.choiceNote}</li>
      </ul>

      <div className="assessment-options">
        {[t.optionYes, t.optionMaybe, t.optionNo].map((option, index) => (
          <div key={index} className={`option-item option-${option.key}`}>
            <div className="option-title">{option.title}</div>
            <div className="option-description">{option.description}</div>
          </div>
        ))}
      </div>

      <ul className="assessment-notes">
        <li className="important-note">{t.startNote}</li>
      </ul>

      <button 
        className="start-button" 
        onClick={handleStart}
        disabled={isStarting}
      >
        {isStarting ? '正在开始...' : t.startButton}
      </button>
      {showResumeDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px 24px', width: 'min(420px, 90vw)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '16px', marginBottom: '16px' }}>{t.resumeDialog?.message || '检测到已有部分作答记录。请选择接续答题或重新开始。'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <button
                onClick={() => { setShowResumeDialog(false); navigate(`/quiz?defaultLang=${defaultLang}&resume=1`); }}
                className="start-button"
                style={{ width: '100%' }}
              >
                {t.resumeDialog?.continueButton || '接续答题'}
              </button>
              <button
                onClick={handleRestart}
                className="start-button"
                style={{ width: '100%', background: '#dc2626', color: '#fff', border: 'none' }}
                disabled={isStarting}
              >
                {t.resumeDialog?.restartButton || '重新开始'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssessmentIntro;
